import { useEffect, useMemo, useState } from "react";

import { SelectModelDropdown } from "../ui/ModelFloatingCard";

import { ModelDropdownProps } from "./types";
import { getModelMetadata } from "./utils";

import { DECISIONS_API } from "@/components/ModelParameters/constants";
import { Model, ModelCatalog } from "@/queries/models";
import { ModelMetadataEntry } from "@/queries/models/useModelMetadata";

const catalogToMetadata = (catalog: ModelCatalog, modelName: string): ModelMetadataEntry => ({
  name: modelName,
  description: catalog.description,
  context_length: catalog.context_length,
  price_per_1m_tokens_input: catalog.pricing_input_per_1m,
  price_per_1m_tokens_output: catalog.pricing_output_per_1m,
  input_modalities: catalog.input_modalities ?? ["text"],
  output_modalities: catalog.output_modalities ?? ["text"],
  supported_parameters: catalog.supported_parameters ?? [],
  max_completion_tokens: catalog.max_output_tokens,
  knowledge_cutoff: catalog.knowledge_cutoff,
  latency: catalog.latency,
  deprecation_date: catalog.deprecation_date,
  retirement_date: catalog.retirement_date,
});

export const ModelDropdown = ({
  selectedModel,
  handleSelectModel,
  isChat: _isChat,
  provider,
  models,
  isLoading,
  disabled,
  selectedAPI,
  metadataMap,
  providerMap,
}: ModelDropdownProps) => {
  const [filteredModels, setFilteredModels] = useState<Model[]>(models);

  useEffect(() => {
    setFilteredModels(models);
  }, [models]);

  useEffect(() => {
    let filtered = [...models];

    if (selectedAPI && (provider === "openai" || provider === "openai.azure")) {
      filtered = filtered.filter(m => {
        if (m.supported_apis && m.supported_apis.length > 0) {
          return m.supported_apis.includes(selectedAPI);
        }

        return selectedAPI !== "images" && selectedAPI !== DECISIONS_API;
      });
    }

    if (selectedAPI && provider === "openrouter") {
      filtered = filtered.filter(m => {
        const apis = m.supported_apis ?? [];
        if (selectedAPI === "images") {
          return apis.includes("images");
        }
        if (selectedAPI === "video") {
          return apis.includes("video");
        }
        if (selectedAPI === "speech") {
          return apis.includes("speech");
        }
        if (selectedAPI === DECISIONS_API) {
          return apis.includes(DECISIONS_API);
        }
        return apis.includes("chat") || apis.length === 0;
      });
    }

    setFilteredModels(filtered);

    if (selectedModel && filtered.length > 0) {
      const isSelectedModelAvailable = filtered.some(
        m => (m.display_name ?? m.llm_model_name) === selectedModel,
      );

      const wasInOriginalList = models.some(
        m => (m.display_name ?? m.llm_model_name) === selectedModel,
      );

      if (!isSelectedModelAvailable && wasInOriginalList) {
        handleSelectModel("");
      }
    }
  }, [models, provider, selectedAPI, selectedModel, handleSelectModel]);

  const visibleModels = filteredModels.filter(m => !m.is_internal);
  const modelNames = visibleModels.map(m => m.display_name ?? m.llm_model_name);

  const optionGroups = useMemo(() => {
    // Skip capability sub-headers when the list is already scoped by selectedAPI.
    const apiFiltered =
      Boolean(selectedAPI) &&
      (provider === "openai" || provider === "openai.azure" || provider === "openrouter");
    if (apiFiltered) {
      return undefined;
    }

    const capabilityOf = (m: Model): "chat" | "image" | "video" | "speech" => {
      const apis = m.supported_apis ?? [];
      if (apis.includes("video")) {
        return "video";
      }
      if (apis.includes("speech")) {
        return "speech";
      }
      if (apis.includes("images")) {
        return "image";
      }
      return "chat";
    };

    const buckets: Record<"chat" | "image" | "video" | "speech", string[]> = {
      chat: [],
      image: [],
      video: [],
      speech: [],
    };
    for (const m of visibleModels) {
      buckets[capabilityOf(m)].push(m.display_name ?? m.llm_model_name);
    }

    const present = (["chat", "image", "video", "speech"] as const).filter(
      c => buckets[c].length > 0,
    );
    if (present.length <= 1) {
      return undefined; // single capability → keep the plain flat list
    }

    const LABELS: Record<"chat" | "image" | "video" | "speech", string> = {
      chat: "Chat",
      image: "Image generation",
      video: "Video generation",
      speech: "Speech",
    };
    return present.map(c => ({ label: LABELS[c], options: buckets[c] }));
  }, [visibleModels, selectedAPI, provider]);

  const perModelMetadata = useMemo(() => {
    const map: Record<string, ModelMetadataEntry | null> = {};
    for (const m of visibleModels) {
      const displayName = m.display_name ?? m.llm_model_name;
      if (m.model_catalog) {
        map[displayName] = catalogToMetadata(m.model_catalog, displayName);
      } else if (metadataMap) {
        map[displayName] = getModelMetadata(provider, m.llm_model_name, metadataMap, providerMap);
      }
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [metadataMap, providerMap, visibleModels, provider]);

  return (
    <SelectModelDropdown
      model={selectedModel || null}
      setModel={name => {
        handleSelectModel(name || "");
      }}
      isLoading={isLoading}
      options={modelNames}
      width="full"
      size="lg"
      disabled={disabled}
      modelMetadata={perModelMetadata}
      optionGroups={optionGroups}
      provider={provider}
    />
  );
};
